import { fmtDate } from '@/lib/dates.js';
import { useState } from 'react';

import { createPortal } from 'react-dom';
import { Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import {
  ShieldCheck,
  FileText,
  AlertTriangle,
  History,
  Loader2,
  MousePointer2,
} from 'lucide-react';
import { consent, type ConsentDocumentRow } from '@/api/client';
import { DragDropUpload } from '@/components/ui/DragDropUpload';
import { useHasRole } from '@/components/RoleGate';

// Roles allowed to open ConsentFieldLayoutEditor — mirrors EDIT_ROLES in
// pages/consent/ConsentFieldLayoutEditor.tsx. Keep in sync.
const CAN_EDIT_LAYOUT_ROLES = [
  'principal_investigator',
  'sub_investigator',
  'coordinator',
  'regulatory_specialist',
  'site_study_designer',
  'site_admin',
] as const;

// PDF-only — see ConsentDocumentsPanel.tsx for the rationale. Both upload
// surfaces must stay aligned with the backend's `/consent/documents/presign`
// allowlist (which rejects everything except application/pdf).
const ACCEPT = 'application/pdf';

export function ConsentUploadCard({
  studyId,
  onRefresh,
}: {
  studyId: string;
  onRefresh: () => void;
}) {
  const qc = useQueryClient();
  const { data, isLoading } = useQuery({
    queryKey: ['consent', 'documents', studyId],
    queryFn: () => consent.listDocuments(studyId),
    enabled: !!studyId,
  });
  const docs = data?.documents ?? [];
  const active = docs.find((d) => d.is_active);
  // Feedback #4 approval flow: upload lands in 'pending_approval' and only
  // goes live once the PI on the study approves (Harish, 2 Oct 2026). Surface that
  // here — otherwise the card reads "no active ICF" while versions sit in
  // the review queue, and the coordinator uploads yet another one.
  const pending = docs.filter((d) => d.approval_status === 'pending_approval');
  const rejected = docs.filter((d) => d.approval_status === 'rejected');

  const [file, setFile] = useState<File | null>(null);
  const [versionDate, setVersionDate] = useState('');
  const [progress, setProgress] = useState(0);
  const [showHistory, setShowHistory] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);

  const reset = () => {
    setFile(null);
    setVersionDate('');
    setProgress(0);
  };

  const upload = useMutation({
    mutationFn: async (): Promise<{ version_label: string; re_consent_flagged: number }> => {
      if (!file) throw new Error('No file selected');

      const presigned = await consent.presignDocument({
        study_id: studyId,
        file_name: file.name,
        mime_type: file.type || guessMime(file.name),
      });

      await uploadWithProgress(
        presigned.upload_url,
        file,
        file.type || guessMime(file.name),
        setProgress,
      );

      const finalised = await consent.finaliseDocument({
        consent_document_id: presigned.consent_document_id,
        study_id: studyId,
        file_name: file.name,
        file_size: file.size,
        mime_type: file.type || guessMime(file.name),
        s3_key: presigned.s3_key,
        // version_label omitted — server mints next-version per
        // [[auto-generate-identifiers]] (v1.0 → v1.1 → …).
        version_date: versionDate || null,
      });

      return finalised;
    },
    onSuccess: (result) => {
      qc.invalidateQueries({ queryKey: ['consent', 'documents', studyId] });
      qc.invalidateQueries({ queryKey: ['signature-queue'] });
      onRefresh();
      // Upload does NOT activate any more — don't claim it did. Re-consent
      // flagging also happens at approval time, not here.
      toast.success(
        `${result.version_label} uploaded and sent for approval. It becomes the active ICF once approved.`,
      );
      reset();
    },
    onError: (e: unknown) =>
      toast.error(`Upload failed: ${e instanceof Error ? e.message : String(e)}`),
  });

  function handleSubmit() {
    if (!file) {
      toast.error('Pick a file first.');
      return;
    }
    if (active) {
      setShowConfirm(true);
    } else {
      upload.mutate();
    }
  }

  return (
    <div className="mt-2 space-y-3 rounded-md border border-surface-border bg-white p-3">
      {/* Active doc summary */}
      {!upload.isPending && active && <ActiveDocSummary doc={active} />}

      {/* Awaiting-approval callout. Without this the card shows an empty
          drop zone and reads as "nothing uploaded yet", when in fact the
          version is uploaded and simply un-approved. */}
      {!upload.isPending && pending.length > 0 && (
        <div className="rounded border border-amber-300 bg-amber-50 p-2.5 text-xs">
          <div className="flex items-start gap-2">
            <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0 text-amber-700" />
            <div className="flex-1">
              <p className="font-medium text-amber-900">
                {pending.length === 1
                  ? `ICF ${pending[0]!.version_label} is awaiting approval`
                  : `${pending.length} ICF versions are awaiting approval`}
              </p>
              <p className="mt-0.5 text-amber-800">
                Uploading no longer activates a version. The PI on this study must approve
                {pending.length === 1
                  ? ' it'
                  : ` one of ${pending.map((d) => d.version_label).join(', ')}`}{' '}
                before eConsent turns Ready.
              </p>
              <Link
                to={`/studies/${studyId}#consent-documents`}
                className="mt-1.5 inline-block font-medium text-amber-900 underline"
              >
                Review &amp; approve →
              </Link>
            </div>
          </div>
        </div>
      )}

      {!upload.isPending && !active && pending.length === 0 && rejected.length > 0 && (
        <div className="rounded border border-red-300 bg-red-50 p-2.5 text-xs text-red-800">
          ICF {rejected.map((d) => d.version_label).join(', ')} was rejected. Upload a new version —
          a rejected one can&apos;t be approved.
        </div>
      )}
      {isLoading && !active && (
        <div className="flex items-center gap-2 text-xs text-ink-muted">
          <Loader2 className="h-3 w-3 animate-spin" /> Loading…
        </div>
      )}

      {/* Drop zone */}
      {!upload.isPending && (
        <DragDropUpload
          accept={ACCEPT}
          acceptHint="PDF, max 50 MB"
          disabled={upload.isPending}
          onSelect={setFile}
          currentFileName={file?.name ?? null}
          onClear={() => setFile(null)}
        />
      )}

      {/* Metadata fields — version label is auto-generated on save
          (see [[auto-generate-identifiers]]). Only the IRB version date
          remains user-provided. */}
      {!upload.isPending && (
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <div className="text-xs">
            <span className="font-medium text-ink-secondary">Version label</span>
            <div className="mt-1 flex items-center rounded border border-dashed border-surface-border bg-surface-muted/30 px-3 py-2 text-sm text-ink-muted">
              {active ? (
                <>
                  Next after{' '}
                  <span className="font-mono mx-1 text-ink-secondary">{active.version_label}</span>{' '}
                  — auto-generated on save.
                </>
              ) : (
                <>
                  Auto-generated as <span className="font-mono mx-1 text-ink-secondary">v1.0</span>{' '}
                  on save.
                </>
              )}
            </div>
          </div>

          <label className="block text-xs">
            <span className="font-medium text-ink-secondary">Version date (printed on ICF)</span>
            <input
              type="date"
              value={versionDate}
              onChange={(e) => setVersionDate(e.target.value)}
              className="mt-1 w-full rounded border border-surface-border px-3 py-2 text-sm"
            />
          </label>
        </div>
      )}

      {/* Upload progress */}
      {upload.isPending && (
        <div className="space-y-1">
          <div className="flex items-center gap-2 text-[11px] text-ink-muted">
            <Loader2 className="h-3 w-3 animate-spin" />
            Uploading… {progress}%
          </div>
          <div className="h-1 w-full overflow-hidden rounded bg-surface-muted">
            <div
              className="h-full bg-accent-500 transition-all"
              style={{ width: `${progress}%` }}
            />
          </div>
        </div>
      )}

      <div className="flex items-center gap-3">
        <button
          type="button"
          disabled={!file || upload.isPending}
          onClick={handleSubmit}
          className="rounded bg-accent-600 px-4 py-2 text-sm font-medium text-white hover:bg-accent-700 disabled:opacity-50"
        >
          {/* Not "Upload & Activate" — upload only submits for approval. */}
          {upload.isPending ? 'Uploading…' : active ? 'Upload New Version' : 'Upload for approval'}
        </button>

        {docs.length > 0 && (
          <button
            type="button"
            className="inline-flex items-center gap-1.5 text-xs text-ink-muted hover:text-ink-primary"
            onClick={() => setShowHistory((v) => !v)}
          >
            <History className="h-3.5 w-3.5" />
            {showHistory ? 'Hide history' : `Show history (${docs.length})`}
          </button>
        )}
      </div>

      {/* Version history */}
      {showHistory && docs.length > 0 && (
        <div className="rounded border border-surface-border overflow-hidden">
          <div className="border-b border-surface-border bg-surface-muted/40 px-3 py-1.5 text-[10px] uppercase tracking-wider text-ink-muted">
            Version history
          </div>
          <ul className="divide-y divide-surface-border">
            {docs.map((d) => (
              <HistoryRow key={d.consent_document_id} doc={d} />
            ))}
          </ul>
        </div>
      )}

      {showConfirm && active && (
        <ReConsentConfirmModal
          activeVersion={active.version_label}
          onCancel={() => setShowConfirm(false)}
          onConfirm={() => {
            setShowConfirm(false);
            upload.mutate();
          }}
        />
      )}
    </div>
  );
}

function ActiveDocSummary({ doc }: { doc: ConsentDocumentRow }) {
  const canEditLayout = useHasRole(CAN_EDIT_LAYOUT_ROLES);
  const hasLayout = doc.has_drag_drop_layout === 'true';
  return (
    <div className="flex items-start gap-2.5 rounded border border-emerald-200 bg-emerald-50/60 px-3 py-2 text-xs">
      <ShieldCheck className="mt-0.5 h-3.5 w-3.5 shrink-0 text-emerald-700" />
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-1.5">
          <span className="font-medium text-emerald-800">ICF {doc.version_label}</span>
          <span className="rounded bg-emerald-100 px-1.5 py-0.5 text-[10px] font-semibold text-emerald-700">
            Active
          </span>
          {!hasLayout && (
            <span
              className="rounded bg-amber-100 px-1.5 py-0.5 text-[10px] font-semibold text-amber-800"
              title="Subjects can’t sign until you place signature fields via the layout editor."
            >
              No signature layout
            </span>
          )}
          {/* Place/edit-signatures link — role-gated so read-only viewers
              (auditor / monitor / compliance) don't land on a locked editor. */}
          {canEditLayout && (
            <Link
              to={`/consent-documents/${doc.consent_document_id}/field-layout`}
              className="inline-flex items-center gap-0.5 rounded border border-accent-300 bg-accent-50 px-1.5 py-0.5 text-[10px] font-semibold text-accent-700 hover:bg-accent-100"
              title={
                hasLayout
                  ? 'Edit signature/coordinator field placement on the PDF'
                  : 'Place signature/coordinator fields on the PDF so subjects can sign'
              }
            >
              <MousePointer2 className="h-2.5 w-2.5" />
              {hasLayout ? 'Edit layout' : 'Place signatures'}
            </Link>
          )}
        </div>
        <div className="mt-0.5 truncate text-[11px] text-emerald-700/80">
          {doc.file_name}
          {doc.version_date && ` · Version date: ${doc.version_date.slice(0, 10)}`}
        </div>
      </div>
    </div>
  );
}

/**
 * Approval state for a non-active version. `approval_status` is absent on
 * rows predating the Feedback #4 flow — those were backfilled to 'approved',
 * so a missing value means "superseded", which needs no badge.
 */
function HistoryStatusBadge({ status }: { status: ConsentDocumentRow['approval_status'] }) {
  if (status === 'pending_approval') {
    return (
      <span className="rounded bg-amber-100 px-1.5 py-0.5 text-[10px] font-semibold text-amber-800">
        Awaiting approval
      </span>
    );
  }
  if (status === 'rejected') {
    return (
      <span className="rounded bg-red-100 px-1.5 py-0.5 text-[10px] font-semibold text-red-700">
        Rejected
      </span>
    );
  }
  return null;
}

function HistoryRow({ doc }: { doc: ConsentDocumentRow }) {
  return (
    <li className="flex items-center gap-2 px-3 py-2 text-xs">
      <FileText className="h-3.5 w-3.5 shrink-0 text-ink-muted" />
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-1.5">
          <span className="font-medium text-ink-primary">{doc.version_label}</span>
          {doc.is_active ? (
            <span className="rounded bg-accent-100 px-1.5 py-0.5 text-[10px] font-semibold text-accent-700">
              Active
            </span>
          ) : (
            // Every non-active row used to render bare, so a pending version
            // was indistinguishable from a superseded one.
            <HistoryStatusBadge status={doc.approval_status} />
          )}
        </div>
        <div className="truncate text-[11px] text-ink-muted">
          {doc.file_name} · {Math.round(doc.file_size / 1024)} KB · {fmtDate(doc.uploaded_at)}
        </div>
      </div>
    </li>
  );
}

function ReConsentConfirmModal({
  activeVersion,
  onCancel,
  onConfirm,
}: {
  activeVersion: string;
  onCancel: () => void;
  onConfirm: () => void;
}) {
  return createPortal(
    <div className="fixed inset-0 z-50 flex items-center justify-center !m-0 p-4">
      <div
        className="absolute inset-0 bg-black/60 backdrop-blur-sm"
        onClick={onCancel}
        aria-hidden
      />
      <div className="relative w-full max-w-md rounded-lg border border-surface-border bg-surface-card p-5 shadow-xl">
        <div className="flex items-start gap-3">
          <AlertTriangle className="h-5 w-5 flex-shrink-0 text-amber-600" />
          <div className="space-y-2">
            <h3 className="text-sm font-semibold text-ink-primary">
              Activate new consent version?
            </h3>
            <p className="text-xs text-ink-muted">
              Activating this document will supersede{' '}
              <span className="font-medium text-ink-primary">{activeVersion}</span>. Every
              currently-enrolled subject on this study will be flagged for re-consent and added to
              the eSignature queue. They cannot have new visits or data entry until they re-consent.
            </p>
            <p className="text-xs text-ink-muted">
              This action is logged in the audit trail and cannot be reverted.
            </p>
          </div>
        </div>
        <div className="mt-4 flex justify-end gap-2">
          <button
            type="button"
            onClick={onCancel}
            className="rounded border border-surface-border px-3 py-1.5 text-xs font-medium hover:bg-surface-muted/50"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={onConfirm}
            className="rounded bg-amber-700 px-3 py-1.5 text-xs font-medium text-white hover:bg-amber-800"
          >
            Yes, activate & flag re-consent
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}

function guessMime(name: string): string {
  const ext = name.toLowerCase().split('.').pop();
  if (ext === 'pdf') return 'application/pdf';
  if (ext === 'docx')
    return 'application/vnd.openxmlformats-officedocument.wordprocessingml.document';
  return 'application/octet-stream';
}

function uploadWithProgress(
  url: string,
  file: File,
  contentType: string,
  onProgress: (pct: number) => void,
): Promise<void> {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open('PUT', url);
    xhr.setRequestHeader('Content-Type', contentType);
    xhr.setRequestHeader('x-amz-server-side-encryption', 'AES256');
    xhr.upload.onprogress = (e) => {
      if (e.lengthComputable) onProgress(Math.round((e.loaded / e.total) * 100));
    };
    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        onProgress(100);
        resolve();
      } else {
        reject(new Error(`S3 PUT failed: ${xhr.status} ${xhr.statusText}`));
      }
    };
    xhr.onerror = () => reject(new Error('Network error during upload'));
    xhr.send(file);
  });
}
